// Iter79 — Forced-password-change screen. Shown when `user.must_change_password`
// is true (admin-created user, freshly-reset password). Until the user picks
// a new password, ALL other app surfaces are blocked.
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ForcePasswordChange = () => {
  const { user, refreshMe, logout } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (newPw.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        new_password: newPw, confirm_password: confirmPw,
      });
      addToast({ type: "success", title: "Password updated", body: "Welcome to SITROOM" });
      // Refresh /me so must_change_password becomes false in context.
      await refreshMe?.();
      // Hard reload as a safe fallback if refreshMe is unavailable.
      if (!refreshMe) window.location.reload();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg-base, #08090d)" }} data-testid="force-pw-change">
      <div className="rounded-md p-6" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)", width: "min(420px, 96vw)" }}>
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 22, color: "var(--text, #f0f2f8)", letterSpacing: "-0.4px" }}>
          Set your password
        </div>
        <div className="mt-1" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }}>
          You're using a temporary password. Pick a new one (≥8 characters) to continue.
        </div>
        <div className="mt-1" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9ec3ec" }}>
          {user?.email}
        </div>

        <div className="mt-4">
          <label style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>New password</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoFocus
            className="w-full px-3 py-2 rounded mt-1"
            style={{ background: "var(--bg3, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", fontFamily: "DM Mono, monospace", fontSize: 13, outline: "none" }}
            data-testid="force-pw-new" />
        </div>
        <div className="mt-3">
          <label style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase" }}>Confirm</label>
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            className="w-full px-3 py-2 rounded mt-1"
            style={{ background: "var(--bg3, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", fontFamily: "DM Mono, monospace", fontSize: 13, outline: "none" }}
            data-testid="force-pw-confirm" />
        </div>

        {error && (
          <div className="mt-3 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="force-pw-error">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <button type="button" onClick={() => logout?.()} style={{ background: "transparent", border: "none", color: "#9aa1b3", fontFamily: "DM Mono, monospace", fontSize: 11, cursor: "pointer" }} data-testid="force-pw-logout">
            Sign out
          </button>
          <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 rounded"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, border: "none" }}
            data-testid="force-pw-submit">
            {busy ? "Saving…" : "Set password"}
          </button>
        </div>
      </div>
    </div>
  );
};
